import classNames from "classnames"

import Badge from "@components/elements/V2/Badge"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import Typography from "@components/elements/V2/Typography"
import PlanFeature from "@components/V2/Cards/Plans/PlanFeature"
import PlanRatesTable from "@components/V2/Cards/Plans/PlanRatesTable"
import LayoutItem from "@components/V2/Layouts/LayoutItem"
import { Color } from "@constants/V2/color"
import {
  getAccentColor,
  getBackgroundColorClass,
  getBorderColorClass,
  getGradientFromColorClass,
  getGradientToColorClass,
  getGradientViaColorClass,
} from "@utils/V2/color"
import React from "react"

export type PlanConfigData = Omit<Storyblok.PlanConfig["content"], "component">

type Props = {
  plan: PlanConfigData
  featuresTitle?: string
  features: Storyblok.PlanInfoCard["features"]
  primaryBackgroundColor: Color
  secondaryBackgroundColor?: Color
  borderColor?: Color
  bannerText?: string
  showBanner: boolean
  ctaType: "outlined" | "solid"
  ctaText: string
  ctaLink: YocoCom.YocoLink | undefined
  ctaColor: Color
  mobileGridOrder?: number
  tracking?: {
    event?: string
    eventKey?: string
    eventValue?: string
  }
  /**
   * When set (by PlanGridSection), the card opts into CSS subgrid alignment at
   * `sm:`+ (wherever cards sit side by side, whether 2- or 3-up) so matching
   * detail-section headings line up across cards in the same row. The value is
   * the total number of shared row tracks the card spans.
   */
  subgridTrackCount?: number
  reserveBannerTrack?: boolean
}

const PlanInfoCard = ({
  plan,
  featuresTitle,
  features,
  primaryBackgroundColor,
  secondaryBackgroundColor,
  borderColor,
  bannerText,
  showBanner,
  ctaType,
  ctaText,
  ctaLink,
  ctaColor,
  mobileGridOrder,
  subgridTrackCount,
  reserveBannerTrack,
  tracking,
  ...props
}: Props) => {
  const accentColor = getAccentColor(primaryBackgroundColor)
  const ctaStyle = ctaType === "solid" ? "solid" : "bordered"
  const badge = plan.badge?.[0]

  const isSubgrid = subgridTrackCount != null

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      style={
        isSubgrid
          ? ({ "--plan-rows": subgridTrackCount } as React.CSSProperties)
          : undefined
      }
      className={classNames(
        // Lift the whole card above sibling cards while a tooltip is open so
        // the bubble (which overflows onto neighbouring cards) isn't obscured —
        // its z-index can't escape this card's stacking context on its own.
        // `:checked` covers tap-to-open (< md); `hover` covers hover-open (md+).
        "group/banner rounded-lg relative z-0 col-span-4 flex flex-col has-[:checked]:z-10 sm:col-span-2 md:col-span-3 md:hover:z-10 lg:col-span-4",
        isSubgrid &&
          "sm:grid sm:grid-rows-subgrid sm:gap-y-0 sm:[grid-row-end:span_var(--plan-rows)]",
        showBanner && "pt-1"
      )}
      {...props}
    >
      {showBanner && (
        <div
          aria-hidden="true"
          className={classNames(
            "rounded-lg pointer-events-none absolute inset-0 -z-10 bg-charcoal/80 opacity-100 shadow-[0px_10px_10px_0px_rgba(0,0,0,0.05)] transition-opacity duration-300 md:opacity-0 md:group-hover/banner:opacity-100"
          )}
        />
      )}

      {(!isSubgrid || reserveBannerTrack) && (
        <div
          className={classNames(
            "flex min-h-[2.25rem] items-center justify-center px-8 text-center",
            isSubgrid && "sm:row-start-1",
            showBanner &&
              "opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover/banner:opacity-100"
          )}
        >
          {showBanner && bannerText && (
            <Typography
              size="body-sm"
              font="grotesk"
              weight="medium"
              color={Color.White}
              text={bannerText}
            />
          )}
        </div>
      )}

      <div
        className={classNames(
          secondaryBackgroundColor
            ? [
                "bg-gradient-to-r",
                getGradientFromColorClass(primaryBackgroundColor),
                getGradientViaColorClass(primaryBackgroundColor),
                getGradientToColorClass(secondaryBackgroundColor),
              ]
            : getBackgroundColorClass(primaryBackgroundColor),
          "rounded-lg flex flex-1 flex-col gap-24 border-[1.5px] px-20 py-32 md:px-32",
          borderColor && getBorderColorClass(borderColor),
          isSubgrid && [
            "sm:grid sm:grid-rows-subgrid sm:gap-y-0",
            reserveBannerTrack ? "sm:row-[2/-1]" : "sm:row-[1/-1]",
          ],
          !showBanner && "shadow-[0px_10px_10px_0px_rgba(0,0,0,0.05)]"
        )}
      >
        <div className="space-y-16">
          <div>
            <div className="flex min-h-[25.81px] items-center justify-between">
              <div className="flex gap-12">
                {plan.icon?.filename && (
                  <GatsbyStoryblokImage
                    image={plan.icon?.filename}
                    alt={plan.icon?.alt}
                    className="mt-[5px] h-24 w-24 shrink-0"
                  />
                )}

                <Typography
                  size="lead-md-fixed"
                  font="grotesk"
                  weight="medium"
                  color={accentColor}
                  text={plan.title}
                />
              </div>

              {badge?.text && (
                <Badge
                  style="solid"
                  size="body-sm"
                  uppercaseText={false}
                  text={badge.text}
                  highlightedText={badge.highlightedText}
                  textColor={badge.textColor ?? accentColor}
                  accentColor={badge.color ?? Color.White}
                  className="!px-12"
                />
              )}
            </div>

            {plan.subtitle && (
              <Typography
                size="body-sm"
                font="grotesk"
                weight="book"
                color={accentColor}
                text={plan.subtitle}
              />
            )}
          </div>

          {(plan.descriptionTitle || plan.description) && (
            <div className="space-y-4 sm:h-[70px]">
              {plan.descriptionTitle && (
                <Typography
                  size="body-md"
                  font="grotesk"
                  weight="medium"
                  color={accentColor}
                  text={plan.descriptionTitle}
                />
              )}
              {plan.description && (
                <Typography
                  size="body-sm"
                  font="grotesk"
                  weight="book"
                  color={accentColor}
                  text={plan.description}
                />
              )}
            </div>
          )}

          <div className="flex items-baseline justify-between gap-4">
            <div>
              <div className="flex items-end">
                <Typography
                  size="h5"
                  font="grotesk"
                  weight="medium"
                  color={accentColor}
                  text={plan.monthlyCost}
                  className="text-nowrap"
                />
                <Typography
                  size="body-md"
                  font="grotesk"
                  weight="medium"
                  color={accentColor}
                  text="/mo"
                  className="pb-8 pl-2"
                />
              </div>
              {plan.priceSubscript && (
                <Typography
                  size="subscript-sm"
                  font="grotesk"
                  weight="book"
                  color={accentColor}
                  text={plan.priceSubscript}
                  className="opacity-80"
                />
              )}
            </div>

            {plan.fromAmount && (
              <div className="pb-8 text-right">
                <div className="flex items-baseline justify-end gap-2">
                  <Typography
                    size="body-sm"
                    font="grotesk"
                    weight="book"
                    color={accentColor}
                    text="From"
                  />
                  <Typography
                    size="lead-sm"
                    font="grotesk"
                    weight="medium"
                    color={accentColor}
                    text={plan.fromAmount}
                    className="text-nowrap"
                  />
                </div>
                <Typography
                  size="subscript-sm"
                  font="grotesk"
                  weight="book"
                  color={accentColor}
                  text="In-person local debit cards"
                  className="opacity-80"
                />
              </div>
            )}
          </div>
        </div>

        <PillButton
          size="medium"
          color={ctaColor}
          style={ctaStyle}
          text={ctaText}
          linkUrl={ctaLink}
          trackingEvent={tracking?.event}
          trackingEventKey={tracking?.eventKey}
          trackingEventValue={tracking?.eventValue}
          overrideTextColor={ctaStyle === "solid" ? undefined : accentColor}
          className={classNames("!w-full", isSubgrid && "sm:mt-10")}
        />

        <div className={classNames("space-y-8", isSubgrid && "sm:contents")}>
          {featuresTitle && (
            <div
              className={classNames(
                "flex items-center gap-8",
                isSubgrid && "sm:mt-24"
              )}
            >
              <Typography
                size="subscript-sm"
                font="grotesk"
                weight="medium"
                color={accentColor}
                text={featuresTitle}
                className="text-nowrap uppercase"
              />
            </div>
          )}

          <div className="space-y-8">
            {features?.map((feature) => (
              <PlanFeature
                key={feature._uid}
                blok={feature}
                textColor={accentColor}
              />
            ))}
          </div>
        </div>

        <PlanRatesTable plan={plan} accentColor={accentColor} />
      </div>
    </LayoutItem>
  )
}

export default PlanInfoCard
